import {
	addDays,
	formatDistanceStrict as baseFmtDistanceStrict,
	isAfter,
	isBefore,
	isSameDay,
	isSameMonth,
	isSameWeek,
	isSameYear,
	set,
} from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import Locale from './Locale'

/**
 * Creates a Date object on the given timezone.
 * The usage of the original Date constructor is disallowed as there's no timezone support there, we got to use date-fns-tz.
 * DON'T use this to parse date-only strings (Y-m-d)! Use date-fns.{@link parseISO()} instead.
 * The final date string will have an INCORRECT timezone, since in pure JS the dates are _always_ local time. Thus, if
 * you need to explicitly show the timezone in formatting (e.g. date-fns.format()), don't forget to pass the timeZone
 * option too (pointing to {@link window.DEFAULT_TIMEZONE}); this is already done in {@link FMT.date()}.
 *
 * @see https://github.com/marnusw/date-fns-tz#overview
 * @param tz {string|null|false} The timezone identifier. Comes first, so it's explicit when there's no timezone to use - and then, defaults to {@link window.DEFAULT_TIMEZONE}. A special value of "false" makes it behave as "new Date()", using the local time.
 * @param date {null|Date|string|number} A date object, string representation, or any number of arguments as you see in {@link DateConstructor()}.
 * @returns {Date}
 */
export function newDate(tz, date = null) {
	//allows for "now" dates and "(year, month, day, etc)" calls
	if (date == null) {
		date = new Date() // eslint-disable-line @miovision/disallow-date/no-new-date
	} else if (arguments.length > 2) {
		const dateParts = Array.from(arguments)
		dateParts.shift() //takes the timezone out
		date = new Date(...dateParts) // eslint-disable-line @miovision/disallow-date/no-new-date
	}

	if (tz === false) {
		return date
	} else {
		tz ||= window.DEFAULT_TIMEZONE
		if (tz == null) {
			// console.warn('Default timezone was requested before it was set!')
			tz = 'America/Los_Angeles'
		}
		return utcToZonedTime(date, tz)
	}
}

/**
 * Same as {@link newDate()} with no extra args, but with +1 day :)
 * @param tz
 * @return {Date}
 */
export function tomorrow(tz = null) { return addDays(newDate(tz), 1) }

/**
 * Same as {@link newDate()} with no extra args, but with -1 day :)
 * @param tz
 * @return {Date}
 */
export function yesterday(tz = null) { return addDays(newDate(tz), -1) }

/**
 * Creates a Date object in the given H:M:S, with the default timezone's day.
 * @see newDate
 * @see setTime
 * @param hours {number}
 * @param minutes {number}
 * @param seconds {number}
 * @param tz {?string}
 * @return {Date}
 * @see newDate()
 */
export function newTime(hours, minutes = 0, seconds = 0, tz = null) {
	return setTime(newDate(tz), hours, minutes, seconds)
}

/**
 * Moves the given Date object to a specific time.
 * @see newTime
 * @see startOf
 * @see endOf
 * @param date {Date}
 * @param hours {number}
 * @param minutes {number}
 * @param seconds {number}
 * @return {Date}
 */
export function setTime( date, hours, minutes = 0, seconds = 0) {
	return newDate(false, set(date, { hours, minutes, seconds })) //false so it obeys the tz offset already used
}

export function startOf(date) { return setTime(date, 0) }
export function endOf(date) { return setTime(date, 23, 59, 59) }

/*
 * These two functions use the local timezone as they're mostly for comparisons and time math, and the end result of
 * what it's compared against will always be in the local timezone anyway.
 */
export function startOfToday() { return newTime(0) }
export function endOfToday() { return newTime(23, 59, 59) }

/**
 * For some twisted reason, we got to write our own wrappers for ANY function that depends on the locale.....
 * @see https://date-fns.org/v2.22.1/docs/I18n
 * @return {null|*} Null or a locale import from date-fns
 */
function getDatesLocale() {
	const locale = new Locale()
	const current = locale.current()
	if (Locale.isDefault(current)) {
		return null
	}
	return require(`date-fns/locale/${current}`).default
}

/**
 * @see https://date-fns.org/v2.22.1/docs/formatDistanceStrict
 * @param {Date|Number} date
 * @param {Date|Number} baseDate What to compare with. Defaults to {@link newDate()}
 * @param {object} options={} Accepts addSuffix, unit and roundingMethod. Locale gets properly overridden............. yep, this wrapper is JUST for this.
 * @return {string}
 */
export function formatDistanceStrict(date, baseDate, options = {}) {
	return baseFmtDistanceStrict(date, baseDate, Object.assign(options, { locale: getDatesLocale() }))
}

/*
 * The functions below are a bit complicated on the timezone side. That happens because they depend on knowing when
 * "now" is, and we can't extract the correct timezone from the given Date object - it's digested by date-fns-tz, which
 * spits out a Date object in the local timezone, with the time piece shifted to fit the given timezone.
 */

/**
 * Is the given date in the past? ONLY WORKS WITH THE DEFAULT TIMEZONE!
 * @see https://date-fns.org/v2.16.1/docs/isPast
 * @param date {number|Date}
 * @param tz {?string}
 * @return boolean
 */
export function isPast(date, tz = null) {
	return isBefore(date, newDate(tz))
}

/**
 * Is the given date in the future? ONLY WORKS WITH THE DEFAULT TIMEZONE!
 * @see https://date-fns.org/v2.16.1/docs/isFuture
 * @param date {number|Date}
 * @param tz {?string}
 * @return boolean
 */
export function isFuture(date, tz = null) {
	return isAfter(date, newDate(tz))
}

/**
 * Is the given date today? ONLY WORKS WITH THE DEFAULT TIMEZONE!
 * @see https://date-fns.org/v2.16.1/docs/isToday
 * @param date {number|Date}
 * @param tz {?string}
 * @return boolean
 */
export function isToday(date, tz = null) {
	return isSameDay(date, newDate(tz))
}

/**
 * Is the given date tomorrow? ONLY WORKS WITH THE DEFAULT TIMEZONE!
 * @see https://date-fns.org/v2.16.1/docs/isTomorrow
 * @param date {number|Date}
 * @param tz {?string}
 * @return boolean
 */
export function isTomorrow(date, tz = null) {
	return isSameDay(date, tomorrow(tz))
}

/**
 * Is the given date yesterday? ONLY WORKS WITH THE DEFAULT TIMEZONE!
 * @see https://date-fns.org/v2.16.1/docs/isYesterday
 * @param date {number|Date}
 * @param tz {?string}
 * @return boolean
 */
export function isYesterday(date, tz = null) {
	return isSameDay(date, yesterday(tz))
}

/**
 * Is the given date in this week? ONLY WORKS WITH THE DEFAULT TIMEZONE!
 * @see https://date-fns.org/v2.16.1/docs/isThisWeek
 * @param date {number|Date}
 * @param tz {?string}
 * @return boolean
 */
export function isThisWeek(date, tz = null) {
	return isSameWeek(date, newDate(tz))
}

/**
 * Is the given date in this month? ONLY WORKS WITH THE DEFAULT TIMEZONE!
 * @see https://date-fns.org/v2.16.1/docs/isThisMonth
 * @param date {number|Date}
 * @param tz {?string}
 * @return boolean
 */
export function isThisMonth(date, tz = null) {
	return isSameMonth(date, newDate(tz))
}

/**
 * Is the given date in this year? ONLY WORKS WITH THE DEFAULT TIMEZONE!
 * @see https://date-fns.org/v2.16.1/docs/isThisYear
 * @param date {number|Date}
 * @param tz {?string}
 * @return boolean
 */
export function isThisYear(date, tz = null) {
	return isSameYear(date, newDate(tz))
}
